import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import flatMap from 'lodash/flatMap';
import type { AppId, ApplicationsListEnhanced, ApplicationsListRaw } from '../../ApplicationWrapper.types';
import { fetchProductList } from '../../api/productList/fetchProductList';
import { enhanceApplicationsList } from '../../api/productList/utils';
import { FetchStatus } from '../../api/api.types';

export type OnAppSelect = (id: AppId, event?: MouseEvent) => void;

export interface ApplicationDropdownWrapperContextProviderProps {
  onAppSelect?: OnAppSelect;
  fetchApplicationListCallback?: () => Promise<ApplicationsListRaw>; // to be used by DAM
  children?: ReactNode;
}

export interface ApplicationsDropdownWrapperContext {
  applicationGroupsList: ApplicationsListEnhanced | undefined;
  onAppSelect?: OnAppSelect;
  applicationListFetchStatus: FetchStatus;
  isNoApplications: boolean;
}

const applicationsDropdownWrapperContext = createContext<ApplicationsDropdownWrapperContext | undefined>(undefined);

export const ApplicationDropdownWrapperContextProvider: FC<ApplicationDropdownWrapperContextProviderProps> = ({
  onAppSelect,
  children,
  fetchApplicationListCallback,
}) => {
  const [applicationGroupsList, setApplicationGroupsList] = useState<ApplicationsListEnhanced>();
  const [applicationListFetchStatus, setApplicationListFetchStatus] = useState<FetchStatus>(FetchStatus.Pending);
  const fetchListFn = useMemo(() => fetchApplicationListCallback || fetchProductList, [fetchApplicationListCallback]);

  const onError = useCallback((error: Error) => {
    console.error(error);
    setApplicationListFetchStatus(FetchStatus.Failure);
  }, []);

  const onSuccess = useCallback(() => {
    setApplicationListFetchStatus(FetchStatus.Success);
  }, []);

  useEffect(() => {
    fetchListFn().then(enhanceApplicationsList).then(setApplicationGroupsList).then(onSuccess).catch(onError);
  }, [fetchListFn, onError, onSuccess]);

  const memoizedApplicationListContextValue = useMemo(() => {
    const flatApplicationGroupsList = flatMap(applicationGroupsList);

    return {
      applicationGroupsList,
      onAppSelect,
      applicationListFetchStatus,
      isNoApplications: flatApplicationGroupsList.length === 0,
    };
  }, [onAppSelect, applicationGroupsList, applicationListFetchStatus]);

  return <applicationsDropdownWrapperContext.Provider value={memoizedApplicationListContextValue}>{children}</applicationsDropdownWrapperContext.Provider>;
};

export const useApplicationsDropdownWrapperContext = () => {
  const value = useContext(applicationsDropdownWrapperContext);

  if (!value) {
    throw new Error('You need to wrap your component with ApplicationDropdownWrapperContextProvider');
  }

  return value;
};
